export type DashboardPeriod = 'all' | 'week' | 'month' | 'year' | 'custom';

export const DASHBOARD_PERIOD_OPTIONS: { value: DashboardPeriod; label: string }[] = [
  { value: 'all', label: 'Toutes périodes' },
  { value: 'week', label: '7 derniers jours' },
  { value: 'month', label: 'Ce mois-ci' },
  { value: 'year', label: 'Cette année' },
  { value: 'custom', label: 'Période personnalisée' },
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function resolveDashboardCreatedRange(
  period: DashboardPeriod,
  customFrom: string,
  customTo: string,
): { createdFrom?: string; createdTo?: string } {
  if (period === 'all') return {};

  const now = new Date();

  if (period === 'year') {
    const y = now.getFullYear();
    return { createdFrom: `${y}-01-01`, createdTo: `${y}-12-31` };
  }

  if (period === 'month') {
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const lastDay = new Date(y, m, 0).getDate();
    return { createdFrom: `${y}-${pad(m)}-01`, createdTo: `${y}-${pad(m)}-${pad(lastDay)}` };
  }

  if (period === 'week') {
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { createdFrom: toIsoDate(start), createdTo: toIsoDate(end) };
  }

  if (period === 'custom') {
    if (customFrom && customTo) {
      return { createdFrom: customFrom, createdTo: customTo };
    }
    if (customFrom) return { createdFrom: customFrom };
    if (customTo) return { createdTo: customTo };
  }

  return {};
}

export function formatDashboardPeriodLabel(
  period: DashboardPeriod,
  customFrom: string,
  customTo: string,
): string {
  const opt = DASHBOARD_PERIOD_OPTIONS.find((o) => o.value === period);
  if (period !== 'custom') return opt?.label ?? period;
  if (customFrom && customTo) {
    return `Du ${customFrom.split('-').reverse().join('/')} au ${customTo.split('-').reverse().join('/')}`;
  }
  return opt?.label ?? 'Période personnalisée';
}

type CreatedAtCarrier = {
  createdAt?: string;
  created_at?: string;
};

function toLocalIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseObjectCreatedAt(raw?: string | null): Date | null {
  if (!raw?.trim()) return null;
  const normalized = raw.trim().includes('T') ? raw.trim() : raw.trim().replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Filtre fiable côté client (date locale) — complète l'API si besoin. */
export function filterObjectsByCreatedRange<T extends CreatedAtCarrier>(
  objects: T[],
  range: { createdFrom?: string; createdTo?: string },
): T[] {
  const { createdFrom, createdTo } = range;
  if (!createdFrom && !createdTo) return objects;

  return objects.filter((object) => {
    const created = parseObjectCreatedAt(object.createdAt ?? object.created_at);
    if (!created) return false;
    const localDate = toLocalIsoDate(created);
    if (createdFrom && localDate < createdFrom) return false;
    if (createdTo && localDate > createdTo) return false;
    return true;
  });
}
